import { foldForSearch } from './searchHelper'

/**
 * Turns an RTF/HTML field into plain text, for exporting and for searching.
 *
 * textContent concatenates without inserting whitespace, which is what search needs:
 * "arbets<b>miljö</b>" has to become "arbetsmiljö" or that word is unsearchable. Note it decodes
 * &nbsp; to U+00A0 — foldForSearch folds that back to a plain space.
 */
export const stripHtml = (html: string): string => {
   const div = document.createElement('div')
   div.innerHTML = html ?? ''
   return div.textContent || div.innerText || ''
}

/** Same length, so a match index in `lower` points into `text`. */
export type SearchableText = {
   text: string
   lower: string
}

const BLOCK_BOUNDARY = /<\/(p|div|li|ul|ol|h[1-6]|tr|td|th|blockquote)>|<br\s*\/?>/gi
const WHITESPACE_RUN = /\s+/g

/** Block boundaries become a space, inline tags deliberately do not. Not in stripHtml, to spare the Excel export. */
const toSearchablePlainText = (html: string): string =>
   foldForSearch(stripHtml(html.replace(BLOCK_BOUNDARY, ' $&')))
      .replace(WHITESPACE_RUN, ' ')
      .trim()

/** Keyed on the html itself, not a record id, so edited text can't resolve to a stale entry. */
const cache = new Map<string, SearchableText>()
const CACHE_LIMIT = 5000

export const searchableText = (html: string): SearchableText => {
   const key = html ?? ''
   const cached = cache.get(key)
   if (cached) return cached

   const text = toSearchablePlainText(key)
   const entry: SearchableText = { text, lower: text.toLocaleLowerCase('sv') }

   if (cache.size >= CACHE_LIMIT) cache.clear()
   cache.set(key, entry)

   return entry
}
